import { useQueryClient } from '@tanstack/react-query'
import { pick } from 'lodash'

import {
  getGetBrokersMembersMemberIdCoveredParticipantsQueryKey,
  usePatchBrokersMembersMemberIdCoveredParticipantsId,
  usePostBrokersMembersMemberIdCoveredParticipants
} from 'utils/web_api'
import { type BrokersCoveredParticipant } from 'utils/web_api.schemas'

type CoveredParticipant = BrokersCoveredParticipant

export const ALLOWED_FIELDS = [
  'family_name',
  'given_name',
  'gender',
  'relationship',
  'ssn',
  'dob',
  'tobacco',
  'has_disability',
  'status',
] as const

export function useUpsertCoveredParticipant ({
  memberId,
  planYear,
  participantId,
  onSuccess,
  onError
}: {
  memberId: string,
  planYear: number,
  participantId?: string,
  onSuccess?: () => void,
  onError?: (error: unknown) => void
}) {
  const queryClient = useQueryClient()

  // Use the standardized generated query key for cache invalidation
  const queryKey = getGetBrokersMembersMemberIdCoveredParticipantsQueryKey(
    memberId,
    { plan_year: planYear, include_pending: true }
  )

  // Use generated POST mutation for creating new participants
  const createMutation = usePostBrokersMembersMemberIdCoveredParticipants({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey })
        onSuccess?.()
      },
      onError: (error) => {
        onError?.(error)
      },
    },
  })

  // Use generated PATCH mutation for updating existing participants
  const updateMutation = usePatchBrokersMembersMemberIdCoveredParticipantsId({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey })
        onSuccess?.()
      },
      onError: (error) => {
        onError?.(error)
      },
    },
  })

  return {
    mutate: (data: Partial<CoveredParticipant>) => {
      const filteredData = pick(data, ALLOWED_FIELDS)

      // Ensure required fields are present
      const requiredFields = ['family_name', 'given_name', 'gender', 'relationship', 'dob', 'tobacco'] as const
      const missingFields = requiredFields.filter(field => !(field in filteredData) || filteredData[field] == null)

      if (missingFields.length > 0) {
        onError?.(new Error(`Missing required fields: ${missingFields.join(', ')}`))
        return
      }

      if (participantId) {
        // Update existing participant
        updateMutation.mutate({
          memberId,
          id: participantId,
          data: {
            plan_year: planYear,
            family_name: filteredData.family_name!,
            given_name: filteredData.given_name!,
            gender: filteredData.gender!,
            relationship: filteredData.relationship!,
            dob: filteredData.dob!,
            tobacco: filteredData.tobacco!,
            has_disability: filteredData.has_disability!,
            ssn: filteredData.ssn,
            status: filteredData.status,
          },
        })
      } else {
        // Create new participant
        createMutation.mutate({
          memberId,
          data: {
            plan_year: planYear,
            family_name: filteredData.family_name!,
            given_name: filteredData.given_name!,
            gender: filteredData.gender!,
            relationship: filteredData.relationship!,
            dob: filteredData.dob!,
            tobacco: filteredData.tobacco!,
            has_disability: filteredData.has_disability!,
            ssn: filteredData.ssn,
            status: filteredData.status,
          },
        })
      }
    },
    isLoading: createMutation.isPending || updateMutation.isPending,
    error: createMutation.error || updateMutation.error,
    isError: !!createMutation.error || !!updateMutation.error,
    isSuccess: createMutation.isSuccess || updateMutation.isSuccess,
    data: createMutation.data || updateMutation.data,
  }
}
